import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styles from './Header.module.css';
import Button from '../Button/Button';
import CrownedELXR from '../CrownedELXR/CrownedELXR';

const Header = ({
  logo = 'RippleBet',
  logoHref = '/',
  user,
  onLogout,
  menuItems = [],
  className = '',
  loading = false
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const toggleUserMenu = () => setIsUserMenuOpen(!isUserMenuOpen);

  return (
    <header className={`${styles.header} ${className}`}>
      <div className={styles.container}>
        <div className={styles.wrapper}>
          {/* Logo */}
          <Link to={logoHref} className={styles.logo} aria-label="Home">
            <span className={styles.logoGradient}>
              {logo.includes('ELXR') ? (
                <>
                  <CrownedELXR />
                  {logo.replace('ELXR', '').trim() && (
                    <span> {logo.replace('ELXR', '').trim()}</span>
                  )}
                </>
              ) : (
                logo
              )}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className={styles.desktopNav} aria-label="Main navigation">
            {menuItems.map((item, index) => (
              <Link
                key={index}
                to={item.href}
                className={styles.navLink}
                aria-current={item.active ? 'page' : undefined}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Right Side Actions */}
          <div className={styles.actions}>
            {loading ? (
              <div className={styles.loadingSection}>
                <div className={styles.loadingSkeleton} />
              </div>
            ) : user ? (
              <div className={styles.userSection}>
                <button
                  className={styles.userButton}
                  onClick={toggleUserMenu}
                  aria-expanded={isUserMenuOpen}
                  aria-haspopup="true"
                  aria-label="User menu"
                >
                  <div className={styles.userAvatar}>
                    {user.avatar ? (
                      <img src={user.avatar} alt="" />
                    ) : (
                      <span>{user.name?.charAt(0) || user.email?.charAt(0) || 'U'}</span>
                    )}
                  </div>
                  <span className={styles.userName}>{user.name || user.email}</span>
                  <svg
                    className={styles.chevron}
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4 6L8 10L12 6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>

                {/* User Dropdown Menu */}
                {isUserMenuOpen && (
                  <>
                    <div 
                      className={styles.backdrop} 
                      onClick={() => setIsUserMenuOpen(false)}
                      aria-hidden="true"
                    />
                    <div className={styles.dropdown} role="menu">
                      <div className={styles.dropdownHeader}>
                        <p className={styles.dropdownName}>{user.name || 'User'}</p>
                        <p className={styles.dropdownEmail}>{user.email}</p>
                      </div>
                      <div className={styles.dropdownDivider} />
                      <Link to="/profile" className={styles.dropdownItem} role="menuitem">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M8 8C10.21 8 12 6.21 12 4C12 1.79 10.21 0 8 0C5.79 0 4 1.79 4 4C4 6.21 5.79 8 8 8ZM8 10C5.33 10 0 11.34 0 14V16H16V14C16 11.34 10.67 10 8 10Z" fill="currentColor"/>
                        </svg>
                        Profile
                      </Link>
                      <Link to="/settings" className={styles.dropdownItem} role="menuitem">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M13.3 5.2L14.7 3.8C15.1 3.4 15.1 2.8 14.7 2.4L13.6 1.3C13.2 0.9 12.6 0.9 12.2 1.3L10.8 2.7C10.3 2.5 9.7 2.3 9.1 2.2V0.5C9.1 0 8.7 -0.4 8.2 -0.4H6.6C6.1 -0.4 5.7 0 5.7 0.5V2.2C5.1 2.3 4.5 2.5 4 2.7L2.6 1.3C2.2 0.9 1.6 0.9 1.2 1.3L0.1 2.4C-0.3 2.8 -0.3 3.4 0.1 3.8L1.5 5.2C1.3 5.7 1.1 6.3 1 6.9H-0.7C-1.2 6.9 -1.6 7.3 -1.6 7.8V9.4C-1.6 9.9 -1.2 10.3 -0.7 10.3H1C1.1 10.9 1.3 11.5 1.5 12L0.1 13.4C-0.3 13.8 -0.3 14.4 0.1 14.8L1.2 15.9C1.6 16.3 2.2 16.3 2.6 15.9L4 14.5C4.5 14.7 5.1 14.9 5.7 15V16.7C5.7 17.2 6.1 17.6 6.6 17.6H8.2C8.7 17.6 9.1 17.2 9.1 16.7V15C9.7 14.9 10.3 14.7 10.8 14.5L12.2 15.9C12.6 16.3 13.2 16.3 13.6 15.9L14.7 14.8C15.1 14.4 15.1 13.8 14.7 13.4L13.3 12C13.5 11.5 13.7 10.9 13.8 10.3H15.5C16 10.3 16.4 9.9 16.4 9.4V7.8C16.4 7.3 16 6.9 15.5 6.9H13.8C13.7 6.3 13.5 5.7 13.3 5.2ZM8 11C6.3 11 5 9.7 5 8C5 6.3 6.3 5 8 5C9.7 5 11 6.3 11 8C11 9.7 9.7 11 8 11Z" fill="currentColor"/>
                        </svg>
                        Settings
                      </Link>
                      <div className={styles.dropdownDivider} />
                      <button 
                        className={styles.dropdownItem} 
                        onClick={onLogout}
                        role="menuitem"
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M6 14H3C2.46957 14 1.96086 13.7893 1.58579 13.4142C1.21071 13.0391 1 12.5304 1 12V4C1 3.46957 1.21071 2.96086 1.58579 2.58579C1.96086 2.21071 2.46957 2 3 2H6M11 11L15 8M15 8L11 5M15 8H5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Sign out
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className={styles.authButtons}>
                <Button variant="ghost" size="small" onClick={() => window.location.href = '/login'}>
                  Sign in
                </Button>
                <Button variant="primary" size="small" onClick={() => window.location.href = '/signup'}>
                  Get started
                </Button>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              className={styles.mobileMenuButton}
              onClick={toggleMenu}
              aria-expanded={isMenuOpen}
              aria-label="Toggle navigation menu"
            >
              <span className={styles.hamburger} aria-hidden="true">
                <span></span>
                <span></span>
                <span></span>
              </span>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <>
            <div 
              className={styles.mobileBackdrop} 
              onClick={() => setIsMenuOpen(false)}
              aria-hidden="true"
            />
            <nav className={styles.mobileNav} aria-label="Mobile navigation">
              {menuItems.map((item, index) => (
                <Link
                  key={index}
                  to={item.href}
                  className={styles.mobileNavLink}
                  onClick={() => setIsMenuOpen(false)}
                  aria-current={item.active ? 'page' : undefined}
                >
                  {item.label}
                </Link>
              ))}
              {!user && (
                <>
                  <div className={styles.mobileDivider} />
                  <Link
                    to="/login"
                    className={styles.mobileNavLink}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/signup"
                    className={`${styles.mobileNavLink} ${styles.mobileNavLinkPrimary}`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Get started
                  </Link>
                </>
              )}
            </nav>
          </>
        )}
      </div>
    </header>
  );
};

export default Header; 